import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { coreAnalyticsEvents } from './core.models';
import { AppLauncher } from '@capacitor/app-launcher';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Store } from '@ngrx/store';
// import { MyFormActionSaveCurrentPage } from '@bepatient/my-form-store';
import { CoreActionSaveDeeplinkFormData } from './core.actions';

@Injectable()
export class CoreExternalAppService {
  private store$ = inject<Store<any>>(Store);

  /**
   * Validate if the target is allowed/approved
   * @param target The target to validate
   * @returns boolean indicating if target is allowed
   */
  isTargetAllowed(target: string): boolean {
    // List of approved targets - can be moved to environment config later
    const allowedTargets = ['pvt'];

    const isAllowed = allowedTargets.includes(target);

    if (!isAllowed) {
      console.warn(
        `Target '${target}' is not allowed. Allowed targets: ${allowedTargets.join(
          ', '
        )}`
      );
    }

    return isAllowed;
  }

  async checkAppAvailability(bundleID: string): Promise<any> {
    const res = await AppLauncher.canOpenUrl({ url: bundleID });
    if (res.value && Capacitor.isNativePlatform()) {
      return Promise.resolve(res.value);
    }
    return Promise.reject('Cannot Open App');
  }

  launchApp({ intent, options }): Observable<boolean> {
    const result = new Subject<boolean>();

    this.checkAppAvailability(options.package)
      .then(res => {
        AppLauncher.openUrl({
          url: intent
        })
          .then(() => result.next(true))
          .catch(error => result.error({ error: error, app: options.package }))
          .finally(() => result.complete());
      })
      .catch(err => {
        result.error({ error: err, app: options.package });
        result.complete();
      });

    return result.asObservable();
  }

  /**
   * Set up Browser event listeners for external app assessment completion detection
   */
  setupExternalAppBrowserListeners(uniqueKey: string): void {
    try {
      // Listen for browser finished event (when user dismisses the browser)
      Browser.addListener('browserFinished', () => {
        console.log('In-App browser closed by user');
        // Check if a real deeplink was received recently
        const deeplinkReceived = localStorage.getItem(
          'external-app-deeplink-received'
        );
        const deeplinkTimestamp = localStorage.getItem(
          'external-app-deeplink-timestamp'
        );

        if (deeplinkReceived === 'true' && deeplinkTimestamp) {
          const deeplinkTime = new Date(deeplinkTimestamp).getTime();
          const currentTime = new Date().getTime();
          const timeDiff = currentTime - deeplinkTime;

          if (timeDiff <= 30000) {
            // Within 30 seconds - no additional action needed
            return;
          }
        }

        // If no deeplink was received or it was too long ago, treat as manual close
        // Add a small delay to ensure browser is fully closed before processing
        setTimeout(() => {
          this.handleManualBrowserClose();
        }, 500);
      }).then(listener => {
        // Store the listener handle for cleanup if needed
      });

      // Optional: Listen for page loaded event
      Browser.addListener('browserPageLoaded', () => {
        console.log('External app assessment loaded in browser');
      }).then(listener => {
        // Browser page loaded listener added
      });
    } catch (error) {
      console.error('Error setting up external app browser listeners:', error);
    }
  }

  /**
   * Handle manual browser close (when user clicks close button)
   */
  private handleManualBrowserClose(): void {
    try {
      // Check if there are pending assessments
      const pendingAssessments = this.getPendingExternalAppAssessments();
      const uniqueKeys = Object.keys(pendingAssessments);

      if (uniqueKeys.length === 0) {
        return;
      }

      // Double-check that no deeplink was received recently (to avoid double processing)
      const deeplinkReceived = localStorage.getItem(
        'external-app-deeplink-received'
      );
      if (deeplinkReceived === 'true') {
        return;
      }

      // Mark assessments as completed due to browser close (regardless of actual status)
      uniqueKeys.forEach(uniqueKey => {
        const completionData = {
          completed: true,
          status: 'completed',
          error: '',
          userId: '',
          completedAt: new Date().toISOString(),
          method: 'browser-closed',
          uniqueKey: uniqueKey,
          callbackData: {
            url: 'browser-closed',
            params: {}
          }
        };

        // Store in deeplink-form-completions
        const existingData = this.getDeeplinkFormCompletionData();
        existingData[uniqueKey] = completionData;
        localStorage.setItem(
          'deeplink-form-completions',
          JSON.stringify(existingData)
        );

        // Dispatch action to trigger button state updates (show completed status)
        this.store$.dispatch(
          new CoreActionSaveDeeplinkFormData({
            uniqueKey: uniqueKey,
            completionData: completionData
          })
        );

        // Auto-submit form for this occurrence (now that user is back in app)
        console.log('Form auto-submitting after external app completion');
        this.autoSubmitFormForOccurrence(uniqueKey);
      });
    } catch (error) {
      console.error('Error handling manual browser close:', error);
    }
  }

  /**
   * Auto-submit form when external app assessment is completed
   */
  private autoSubmitFormForOccurrence(uniqueKey: string): void {
    try {
      // Dispatch SAVE_CURRENT_PAGE to follow the normal form submission flow
      // This will trigger the same API call and response handling as manual submission
      // TODO-CIRCULAR-DEPENDENCY
      // this.store$.dispatch(new MyFormActionSaveCurrentPage()); // Commented out to avoid circular dependency
      this.store$.dispatch({ type: '[MyFormAction] Save current page' }); // Using string-based action instead
    } catch (error) {
      console.error('Error auto-submitting form:', error);
    }
  }

  /**
   * Get pending external app assessments from localStorage
   */
  getPendingExternalAppAssessments(): any {
    try {
      const data = localStorage.getItem('external-app-pending-assessments');
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error getting pending external app assessments:', error);
      return {};
    }
  }

  /**
   * Get deeplink form completion data from localStorage
   */
  private getDeeplinkFormCompletionData(): any {
    try {
      const data = localStorage.getItem('deeplink-form-completions');
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error getting deeplink form completion data:', error);
      return {};
    }
  }

  /**
   * Track external app assessment when opened in mobile browser
   */
  trackExternalAppAssessmentForMobile(
    uniqueKey: string,
    assessmentData: any
  ): void {
    try {
      console.log('External app assessment tracked for mobile device');
      const pendingAssessments = this.getPendingExternalAppAssessments();

      // Clean the assessment data to avoid circular references
      const cleanAssessmentData = {
        url: assessmentData.url,
        originalUrl: assessmentData.originalUrl,
        openedAt: assessmentData.openedAt,
        formId: assessmentData.formData?.uuid || 'unknown',
        occurrence: assessmentData.formData?.intPageOccurrence || 0
      };

      pendingAssessments[uniqueKey] = cleanAssessmentData;
      localStorage.setItem(
        'external-app-pending-assessments',
        JSON.stringify(pendingAssessments)
      );
    } catch (error) {
      console.error(
        'Error tracking external app assessment for mobile:',
        error
      );
    }
  }

  /**
   * Remove pending external app assessment from localStorage
   */
  removePendingExternalAppAssessment(uniqueKey: string): void {
    try {
      const pendingAssessments = this.getPendingExternalAppAssessments();
      delete pendingAssessments[uniqueKey];
      localStorage.setItem(
        'external-app-pending-assessments',
        JSON.stringify(pendingAssessments)
      );
    } catch (error) {
      console.error('Error removing pending external app assessment:', error);
    }
  }
}
